import React, { Suspense, lazy, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import MobileMenuButton from "./components/MobileMenuButton";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider as LegacyAuthProvider } from "./context/AuthContext";
import { AuthProvider } from "./context/UserAuthContext";
import { PageVisibilityProvider } from "./context/PageVisibilityContext";
import { SpotifyProvider } from "./context/SpotifyContext";
import { MiniPlayerProvider } from "./context/MiniPlayerContext";
import { LiveTVMiniPlayerProvider } from "./context/LiveTVMiniPlayerContext";
import { AudioPlayerProvider } from "./context/AudioPlayerContext";
import { TaskAlarmProvider } from "./contexts/TaskAlarmContext";
import { TaskTimerProvider } from "./contexts/TaskTimerContext";
import { GlobalTaskMonitorProvider } from "./contexts/GlobalTaskMonitor";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { DemoProvider, ViewOnlyToast } from "./context/DemoContext";
import { useEmailAutoSync } from "./hooks/useEmailAutoSync";
import GlobalMediaPlayerBar from "./components/GlobalMediaPlayerBar";
import GlobalAudioPlayerBar from "./components/GlobalAudioPlayerBar";
import CounterfindAIChat from "./components/CounterfindAIChat";
import SpotifyGlobalSync from "./components/SpotifyGlobalSync";
import MiniVideoPlayer from "./components/MiniVideoPlayer";
import LiveTVMiniPlayer from "./components/LiveTVMiniPlayer";
import NowPlayingModal from "./components/NowPlayingModal";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";

// Authentication Pages
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import PricingPage from "./pages/PricingPage";
import FoodPage from "./pages/FoodPage";
import TicketsPage from "./pages/TicketsPage";
import HealthPage from "./pages/HealthPage";
import Shopping from "./pages/Shopping";
import Inventory from "./pages/Inventory";
import MusicReleases from "./pages/MusicReleases";

// Public Pages
import PublicBeatStore from "./pages/PublicBeatStore";
import PublicPlaylist from "./pages/PublicPlaylist";
import SharedShoppingList from "./pages/SharedShoppingList";
import SharedCallSheet from "./pages/SharedCallSheet";
import SharedStoryboardFolder from "./pages/SharedStoryboardFolder";
import DemoPage from "./pages/DemoPage";
import DemoBanner from "./components/DemoBanner";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import LandingPage from "./pages/LandingPage";

// Authentication & Utilities (Legacy)
import Login from "./pages/Login";
import SpotifyCallback from "./pages/SpotifyCallback";
import SpotifyTest from "./pages/SpotifyTest";

// Main Dashboard
import Dashboard from "./pages/Dashboard";
import RealEstateManagingPartnerDashboardNew from "./pages/RealEstateManagingPartnerDashboardNew";
import BusinessDashboard from "./pages/BusinessDashboard";

// Real Estate Managing Partner Pages
import RealEstateManagingPartnerContacts from "./pages/RealEstateManagingPartnerContacts";
import RealEstateManagingPartnerDocuments from "./pages/RealEstateManagingPartnerDocuments";
import RealEstateManagingPartnerMeetings from "./pages/RealEstateManagingPartnerMeetings";
import RealEstateManagingPartnerCommunications from "./pages/RealEstateManagingPartnerCommunications";
import RealEstateManagingPartnerCalendar from "./pages/RealEstateManagingPartnerCalendar";
import RealEstateManagingPartnerAnalytics from "./pages/RealEstateManagingPartnerAnalytics";
import RealEstateManagingPartnerFinances from "./pages/RealEstateManagingPartnerFinances";
import Budgeting from "./pages/Budgeting";
import AllBillsPage from "./pages/AllBillsPage";
import AllTransactionsPage from "./pages/AllTransactionsPage";
import RealEstateManagingPartnerMarketing from "./pages/RealEstateManagingPartnerMarketing";
import MarketingHub from "./pages/MarketingHub";
import RealEstateManagingPartnerProperties from "./pages/RealEstateManagingPartnerProperties";
import RealEstateManagingPartnerReports from "./pages/RealEstateManagingPartnerReports";
import SettingsAccounts from "./pages/SettingsAccounts";
import CounterfindProductQueue from "./pages/CounterfindProductQueue";

// Real Estate Support Pages
import RealEstateMediaPortfolio from "./pages/RealEstateMediaPortfolio";
import UltimateLeadGeneration from "./pages/UltimateLeadGeneration";
import RealEstateClientsCustomers from "./pages/RealEstateClientsCustomers";
import TalentDatabase from "./pages/TalentDatabase";
import CreativeServices from "./pages/CreativeServices";
import Orders from "./pages/Orders";
import Bookings from "./pages/Bookings";
import CustomerService from "./pages/CustomerService";
import RealEstateCustomerService from "./pages/RealEstateCustomerService";
import ReviewsFeedback from "./pages/ReviewsFeedback";
import LiveScores from "./pages/LiveScores";
import FilmAnalysis from "./pages/FilmAnalysis";
import Rosters from "./pages/Rosters";
import Scouting from "./pages/Scouting";
import GameDayOperations from "./pages/GameDayOperations";
import FacilitiesEquipment from "./pages/FacilitiesEquipment";
import Draft from "./pages/Draft";
import Performance from "./pages/Performance";
import PlayerHealth from "./pages/PlayerHealth";
import HR from "./pages/HR";
import RealEstateAssetsNew from "./pages/RealEstateAssetsNew";
import RealEstateEmployees from "./pages/RealEstateEmployees";
import RealEstateHR from "./pages/RealEstateHR";
import RealEstateDistribution from "./pages/RealEstateDistribution";
import RealEstateAnalytics from "./pages/RealEstateAnalytics";
import RealEstateInvestments from "./pages/RealEstateInvestments";

// Shared/Utility Pages
import MediaPortfolio from "./pages/MediaPortfolio";
import Clipping from "./pages/Clipping";
import MessageCenter from "./pages/MessageCenter";
import EmailCenter from "./pages/EmailCenter";
import Communications from "./pages/Communications";
import OtterAI from "./pages/OtterAI";
import WhisperTranscribe from "./pages/WhisperTranscribe";
import Notes from "./pages/Notes";
import SimpleNotes from "./pages/SimpleNotes";
import MainCalendar from "./pages/MainCalendar";
import Properties from "./pages/Properties";
import Marketing from "./pages/Marketing";
import Distribution from "./pages/Distribution";
import Travel from "./pages/Travel";
import Passwords from "./pages/Passwords";
import MyApps from "./pages/MyApps";
import DocumentsPageWithFolders from "./pages/DocumentsPageWithFolders";
import SocialMedia from "./pages/SocialMedia";
import MediaFactory from "./pages/MediaFactory";
import Tasks from "./pages/Tasks";
import AdminDashboard from "./pages/AdminDashboard";
import CompanyAdmin from "./pages/CompanyAdmin";
import SecurityCameras from "./pages/SecurityCameras";
import TwitchPage from "./pages/TwitchPage";
import Moodboard from "./pages/Moodboard";
import Doomscroll from "./pages/Doomscroll";
import KnowledgeBase from "./pages/KnowledgeBase";
import Transcriptions from "./pages/Transcriptions";
import Itineraries from "./pages/Itineraries";
import SlackPage from "./pages/SlackPage";
import HubSpotPage from "./pages/HubSpotPage";
import OnboardingAdmin from "./pages/OnboardingAdmin";
import OnboardingPublic from "./pages/OnboardingPublic";
import MailboxPage from "./pages/MailboxPage";
import ChatPage from "./pages/ChatPage";
import ComputerUsePage from "./pages/ComputerUsePage";

// Always Elevate Brand Management Pages
import AECommandCenter from "./pages/ae/AECommandCenter";
import AEAdminDashboard from "./pages/ae/AEAdminDashboard";
import AETasks from "./pages/ae/AETasks";
import AEContent from "./pages/ae/AEContent";
import AEMessages from "./pages/ae/AEMessages";
import AEAdminContentStudio from "./pages/ae/AEAdminContentStudio";
import AEAdminTasks from "./pages/ae/AEAdminTasks";
import AECalendar from "./pages/ae/AECalendar";
import AEAssets from "./pages/ae/AEAssets";
import AENotifications from "./pages/ae/AENotifications";
import AEAuditLog from "./pages/ae/AEAuditLog";
import AEPartnerships from "./pages/ae/AEPartnerships";
import AEMedia from "./pages/ae/AEMedia";
import AEEvents from "./pages/ae/AEEvents";
import AEBlueprint from "./pages/ae/AEBlueprint";
import AERoadmap from "./pages/ae/AERoadmap";
import AEReports from "./pages/ae/AEReports";
import AEProjects from "./pages/ae/AEProjects";
import AEClientRequests from "./pages/ae/AEClientRequests";

// Lazy load heavy pages
const Meetings = lazy(() => import("./pages/Meetings"));

// Page names for tracking
const PAGE_NAMES = {
  '/': 'Dashboard', '/dashboard': 'Dashboard', '/contacts': 'Contacts',
  '/chat': 'AI Chat',
  '/communications': 'Communications', '/meetings': 'Meetings', '/documents': 'Documents',
  '/calendar': 'Calendar', '/finances': 'Finances', '/analytics': 'Analytics',
  '/clients': 'Clients & Customers', '/media': 'Media Portfolio', '/tasks': 'Tasks',
  '/properties': 'Properties', '/marketing': 'Marketing', '/employees': 'Employees',
  '/inventory': 'Inventory', '/shopping': 'Shopping', '/settings': 'Settings',
  '/admin': 'Admin Dashboard', '/notes': 'Notes', '/health': 'Health',
};

// Silent page view tracker — fires on every route change
function PageTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    try {
      const userId = localStorage.getItem('userId') || localStorage.getItem('user_id');
      if (!userId || location.pathname === '/auth' || location.pathname === '/login') return;

      const pageName = PAGE_NAMES[location.pathname] ||
        location.pathname.split('/').filter(Boolean).map(p =>
          p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' ')
        ).join(' / ') || 'Unknown';

      // Fire-and-forget — no await, no error UI
      fetch(`${window.location.origin}/api/admin/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action: 'page_view', page: pageName }),
      }).catch(() => {});
    } catch (_e) { /* fire-and-forget tracking — errors silently ignored */ }
  }, [location.pathname]);

  return null;
}

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  return (
    <div className="App bg-[#000000] min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <LegacyAuthProvider>
            <PageVisibilityProvider>
            <AppContent 
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              isSidebarCollapsed={isSidebarCollapsed}
              setIsSidebarCollapsed={setIsSidebarCollapsed}
            />
            </PageVisibilityProvider>
          </LegacyAuthProvider>
        </AuthProvider>
        <Toaster />
        <SonnerToaster richColors position="top-center" />
      </BrowserRouter>
    </div>
  );
}

// Separate component that uses hooks requiring AuthProvider
function AppContent({ isSidebarOpen, setIsSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed }) {
  // Enable global email auto-sync - must be inside AuthProvider
  useEmailAutoSync();
  const location = useLocation();
  const isPreviewMode = new URLSearchParams(location.search).get('preview') === 'true';

  return (
    <SpotifyProvider>
      <AudioPlayerProvider>
      <MiniPlayerProvider>
      <LiveTVMiniPlayerProvider>
      <TaskAlarmProvider>
      <TaskTimerProvider>
      <GlobalTaskMonitorProvider>
      <DemoProvider>
      <PageTracker />
      <DemoBanner />
      <ViewOnlyToast />
      <Routes>
        {/* Auth Routes (Public) */}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        
        {/* Public Routes */}
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/spotify/callback" element={<SpotifyCallback />} />
        <Route path="/spotify-test" element={<SpotifyTest />} />
        
        {/* Public Beat Store - No auth required */}
        <Route path="/beats/:storeId" element={<PublicBeatStore />} />
        <Route path="/playlist/:playlistId" element={<PublicPlaylist />} />
        <Route path="/shared/shopping/:token" element={<SharedShoppingList />} />
        <Route path="/share/callsheet/:shareToken" element={<SharedCallSheet />} />
        <Route path="/share/storyboard/:shareToken" element={<SharedStoryboardFolder />} />
        
        {/* Legal Pages - No auth required */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        
        {/* Public Landing Page - No auth required (for Google Console verification) */}
        <Route path="/home" element={<LandingPage />} />
        <Route path="/welcome" element={<LandingPage />} />
        
        {/* Public Onboarding Forms - No auth required */}
        <Route path="/onboarding/:slug" element={<OnboardingPublic />} />
        
        {/* Demo Mode - No auth required */}
        <Route path="/demo/:templateId" element={<DemoPage />} />

        {/* Main App Routes - Protected */}
        <Route path="/*" element={
          <ProtectedRoute>
            <>
              {!isPreviewMode && (
                <>
                  <Sidebar 
                    isOpen={isSidebarOpen} 
                    setIsOpen={setIsSidebarOpen}
                    isCollapsed={isSidebarCollapsed}
                    setIsCollapsed={setIsSidebarCollapsed}
                  />
                  <MobileMenuButton onClick={() => setIsSidebarOpen(true)} />
                </>
              )}
              <div className={`ml-0 transition-all duration-300 pb-20 ${!isPreviewMode && (isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64')}`}>
                <div>
                  <Routes>
                    {/* Main Dashboard */}
                    <Route path="/" element={<ErrorBoundary><RealEstateManagingPartnerDashboardNew /></ErrorBoundary>} />
                    <Route path="/chat" element={<ChatPage />} />
                  <Route path="/computer-use" element={<ErrorBoundary><ComputerUsePage /></ErrorBoundary>} />
                    <Route path="/counterfind/product-queue" element={<ErrorBoundary><CounterfindProductQueue /></ErrorBoundary>} />
                    <Route path="/dashboard" element={<ErrorBoundary><RealEstateManagingPartnerDashboardNew /></ErrorBoundary>} />
                    <Route path="/dashboard-re" element={<ErrorBoundary><RealEstateManagingPartnerDashboardNew /></ErrorBoundary>} />
                    <Route path="/business-dashboard" element={<ErrorBoundary><BusinessDashboard /></ErrorBoundary>} />
                    
                    {/* Real Estate Managing Partner Core Pages */}
                  <Route path="/contacts-re" element={<ErrorBoundary><RealEstateManagingPartnerContacts /></ErrorBoundary>} />
                  <Route path="/documents-re" element={<ErrorBoundary><RealEstateManagingPartnerDocuments /></ErrorBoundary>} />
                  <Route path="/projects-re" element={<ErrorBoundary><RealEstateManagingPartnerMeetings /></ErrorBoundary>} />
                  <Route path="/communications-re" element={<ErrorBoundary><RealEstateManagingPartnerCommunications /></ErrorBoundary>} />
                  <Route path="/calendar-re" element={<ErrorBoundary><RealEstateManagingPartnerCalendar /></ErrorBoundary>} />
                  <Route path="/analytics" element={<ErrorBoundary><RealEstateManagingPartnerAnalytics /></ErrorBoundary>} />
                  <Route path="/finances-re" element={<ErrorBoundary><RealEstateManagingPartnerFinances /></ErrorBoundary>} />
                  <Route path="/budgeting" element={<ErrorBoundary><Budgeting /></ErrorBoundary>} />
                  <Route path="/all-bills" element={<ErrorBoundary><AllBillsPage /></ErrorBoundary>} />
                  <Route path="/all-transactions" element={<ErrorBoundary><AllTransactionsPage /></ErrorBoundary>} />
                  <Route path="/marketing-re" element={<ErrorBoundary><MarketingHub /></ErrorBoundary>} />
                  <Route path="/properties-re" element={<ErrorBoundary><RealEstateManagingPartnerProperties /></ErrorBoundary>} />
                  <Route path="/reports-re" element={<ErrorBoundary><RealEstateManagingPartnerReports /></ErrorBoundary>} />
                  
                  {/* Real Estate Support Pages */}
                  <Route path="/media-portfolio-re" element={<ErrorBoundary><RealEstateMediaPortfolio /></ErrorBoundary>} />
                  <Route path="/lead-generation" element={<ErrorBoundary><UltimateLeadGeneration /></ErrorBoundary>} />
                  <Route path="/clients-customers-re" element={<ErrorBoundary><RealEstateClientsCustomers /></ErrorBoundary>} />
                  <Route path="/talent-database" element={<ErrorBoundary><TalentDatabase /></ErrorBoundary>} />
                  <Route path="/creative-services" element={<ErrorBoundary><CreativeServices /></ErrorBoundary>} />
                  <Route path="/orders" element={<ErrorBoundary><Orders /></ErrorBoundary>} />
                  <Route path="/bookings" element={<ErrorBoundary><Bookings /></ErrorBoundary>} />
                  <Route path="/customer-service" element={<ErrorBoundary><CustomerService /></ErrorBoundary>} />
                  <Route path="/customer-service-re" element={<ErrorBoundary><RealEstateCustomerService /></ErrorBoundary>} />
                  <Route path="/reviews-feedback" element={<ErrorBoundary><ReviewsFeedback /></ErrorBoundary>} />
                  <Route path="/live-scores" element={<ErrorBoundary><LiveScores /></ErrorBoundary>} />
                  <Route path="/film-analysis" element={<ErrorBoundary><FilmAnalysis /></ErrorBoundary>} />
                  <Route path="/rosters" element={<ErrorBoundary><Rosters /></ErrorBoundary>} />
                  <Route path="/scouting" element={<ErrorBoundary><Scouting /></ErrorBoundary>} />
                  <Route path="/game-day-operations" element={<ErrorBoundary><GameDayOperations /></ErrorBoundary>} />
                  <Route path="/facilities-equipment" element={<ErrorBoundary><FacilitiesEquipment /></ErrorBoundary>} />
                  <Route path="/draft" element={<ErrorBoundary><Draft /></ErrorBoundary>} />
                  <Route path="/performance" element={<ErrorBoundary><Performance /></ErrorBoundary>} />
                  <Route path="/player-health" element={<ErrorBoundary><PlayerHealth /></ErrorBoundary>} />
                  <Route path="/hr" element={<ErrorBoundary><HR /></ErrorBoundary>} />
                  <Route path="/assets-files-re" element={<ErrorBoundary><RealEstateAssetsNew /></ErrorBoundary>} />
                  <Route path="/employees-re" element={<ErrorBoundary><RealEstateEmployees /></ErrorBoundary>} />
                  <Route path="/hr-re" element={<ErrorBoundary><RealEstateHR /></ErrorBoundary>} />
                  <Route path="/distribution-re" element={<ErrorBoundary><RealEstateDistribution /></ErrorBoundary>} />
                  <Route path="/analytics-re" element={<ErrorBoundary><RealEstateAnalytics /></ErrorBoundary>} />
                  <Route path="/investments-re" element={<ErrorBoundary><RealEstateInvestments /></ErrorBoundary>} />
                  
                  {/* Shared/Utility Pages */}
                  <Route path="/media-portfolio" element={<ErrorBoundary><RealEstateMediaPortfolio /></ErrorBoundary>} />
                  <Route path="/message-center" element={<MessageCenter />} />
                  <Route path="/email-center" element={<EmailCenter />} />
                  <Route path="/communications" element={<Communications />} />
                  <Route path="/food" element={<ErrorBoundary><FoodPage /></ErrorBoundary>} />
                  <Route path="/otter-ai" element={<OtterAI />} />
                  <Route path="/transcribe" element={<WhisperTranscribe />} />
                  <Route path="/notes" element={<Notes />} />
                  <Route path="/quick-notes" element={<SimpleNotes />} />
                  <Route path="/calendar" element={<MainCalendar />} />
                  <Route path="/main-calendar" element={<Navigate to="/calendar" replace />} />
                  <Route path="/tasks" element={<ErrorBoundary><Tasks /></ErrorBoundary>} />
                  <Route path="/knowledge" element={<ErrorBoundary><KnowledgeBase /></ErrorBoundary>} />
                  <Route path="/transcriptions" element={<ErrorBoundary><Transcriptions /></ErrorBoundary>} />
                  <Route path="/itineraries" element={<ErrorBoundary><Itineraries /></ErrorBoundary>} />
                  <Route path="/slack" element={<ErrorBoundary><SlackPage /></ErrorBoundary>} />
                  <Route path="/hubspot" element={<ErrorBoundary><HubSpotPage /></ErrorBoundary>} />
                  <Route path="/mailbox" element={<ErrorBoundary><MailboxPage /></ErrorBoundary>} />
                  <Route path="/onboarding-admin" element={<ErrorBoundary><OnboardingAdmin /></ErrorBoundary>} />
                  <Route path="/properties" element={<Properties />} />
                  <Route path="/marketing" element={<Marketing />} />
                  <Route path="/social-media" element={<SocialMedia />} />
                  <Route path="/media-factory" element={<MediaFactory />} />
                  <Route path="/clipping" element={<ErrorBoundary><Clipping /></ErrorBoundary>} />
                  <Route path="/distribution" element={<Distribution />} />
                  <Route path="/travel" element={<Travel />} />
                  <Route path="/security-cameras" element={<ErrorBoundary><SecurityCameras /></ErrorBoundary>} />
                  <Route path="/passwords" element={<Passwords />} />
                  <Route path="/my-apps" element={<MyApps />} />
                  <Route path="/tickets" element={<ErrorBoundary><TicketsPage /></ErrorBoundary>} />
                  <Route path="/health" element={<ErrorBoundary><HealthPage /></ErrorBoundary>} />
                  <Route path="/shopping" element={<ErrorBoundary><Shopping /></ErrorBoundary>} />
                  <Route path="/inventory" element={<ErrorBoundary><Inventory /></ErrorBoundary>} />
                  <Route path="/music-releases" element={<ErrorBoundary><MusicReleases /></ErrorBoundary>} />
                  <Route path="/documents-page" element={<DocumentsPageWithFolders />} />
                  <Route path="/settings" element={<ErrorBoundary><SettingsAccounts /></ErrorBoundary>} />
                  <Route path="/accounts" element={<Navigate to="/settings" replace />} />
                  <Route path="/youtube" element={<Navigate to="/media-factory" replace />} />
                  
                  {/* Visual Planning - Moodboard & Storyboard */}
                  <Route path="/moodboard" element={<ErrorBoundary><Moodboard /></ErrorBoundary>} />
                  <Route path="/doomscroll" element={<ErrorBoundary><Doomscroll /></ErrorBoundary>} />
                  
                  {/* Streaming - User specific */}
                  <Route path="/twitch" element={<ErrorBoundary><TwitchPage /></ErrorBoundary>} />
                  
                  {/* Always Elevate Brand Management - Akayleb & Elle Only */}
                  <Route path="/ae/command-center" element={<ErrorBoundary><AECommandCenter /></ErrorBoundary>} />
                  <Route path="/ae/admin" element={<ErrorBoundary><AEAdminDashboard /></ErrorBoundary>} />
                  <Route path="/ae/blueprint" element={<ErrorBoundary><AEBlueprint /></ErrorBoundary>} />
                  <Route path="/ae/tasks" element={<ErrorBoundary><AETasks /></ErrorBoundary>} />
                  <Route path="/ae/content" element={<ErrorBoundary><AEContent /></ErrorBoundary>} />
                  <Route path="/ae/calendar" element={<ErrorBoundary><AECalendar /></ErrorBoundary>} />
                  <Route path="/ae/partnerships" element={<ErrorBoundary><AEPartnerships /></ErrorBoundary>} />
                  <Route path="/ae/media" element={<ErrorBoundary><AEMedia /></ErrorBoundary>} />
                  <Route path="/ae/community" element={<ErrorBoundary><AECommandCenter /></ErrorBoundary>} />
                  <Route path="/ae/assets" element={<ErrorBoundary><AEAssets /></ErrorBoundary>} />
                  <Route path="/ae/messages" element={<ErrorBoundary><AEMessages /></ErrorBoundary>} />
                  <Route path="/ae/notifications" element={<ErrorBoundary><AENotifications /></ErrorBoundary>} />
                  <Route path="/ae/admin/content" element={<ErrorBoundary><AEAdminContentStudio /></ErrorBoundary>} />
                  <Route path="/ae/admin/strategy" element={<ErrorBoundary><AERoadmap /></ErrorBoundary>} />
                  <Route path="/ae/admin/projects" element={<ErrorBoundary><AEProjects /></ErrorBoundary>} />
                  <Route path="/ae/admin/tasks" element={<ErrorBoundary><AEAdminTasks /></ErrorBoundary>} />
                  <Route path="/ae/admin/partnerships" element={<ErrorBoundary><AEPartnerships /></ErrorBoundary>} />
                  <Route path="/ae/admin/media" element={<ErrorBoundary><AEMedia /></ErrorBoundary>} />
                  <Route path="/ae/admin/events" element={<ErrorBoundary><AEEvents /></ErrorBoundary>} />
                  <Route path="/ae/admin/requests" element={<ErrorBoundary><AEClientRequests /></ErrorBoundary>} />
                  <Route path="/ae/admin/assets" element={<ErrorBoundary><AEAssets /></ErrorBoundary>} />
                  <Route path="/ae/admin/audit-log" element={<ErrorBoundary><AEAuditLog /></ErrorBoundary>} />
                  <Route path="/ae/admin/reports" element={<ErrorBoundary><AEReports /></ErrorBoundary>} />
                  
                  {/* Admin Dashboard - Protected, Admin accounts only */}
                  <Route path="/admin" element={
                    <AdminProtectedRoute>
                      <ErrorBoundary><AdminDashboard /></ErrorBoundary>
                    </AdminProtectedRoute>
                  } />
                  
                  {/* Company Admin - Team Management for Small Business/Enterprise */}
                  <Route path="/team-admin" element={<ErrorBoundary><CompanyAdmin /></ErrorBoundary>} />
                  
                  {/* Redirect old /contacts to /contacts-re */}
                  <Route path="/contacts" element={<Navigate to="/contacts-re" replace />} />
                  
                  {/* Lazy loaded pages */}
                  <Route path="/meetings" element={
                    <Suspense fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                          <p className="text-gray-400">Loading meetings...</p>
                        </div>
                      </div>
                    }>
                      <Meetings />
                    </Suspense>
                  } />
                </Routes>
              </div>
            </div>
          </>
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* Global Spotify sync - enables player bar on all pages */}
      <SpotifyGlobalSync />
      
      {/* Global Spotify Player Bar - MediaFactory design */}
      <GlobalMediaPlayerBar />
      
      {/* Persistent Mini Video Player */}
      <MiniVideoPlayer />
      
      {/* Live TV Mini Player - persists across pages */}
      <LiveTVMiniPlayer />
      
      {/* Now Playing Modal with Lyrics */}
      <NowPlayingModal />
      
      {/* Global Audio Player Bar - for beats/instrumentals */}
      <GlobalAudioPlayerBar />

      {/* Floating AI chat — visible only for counterfind@operatr.com */}
      <CounterfindAIChat />
    </DemoProvider>
    </GlobalTaskMonitorProvider>
    </TaskTimerProvider>
    </TaskAlarmProvider>
    </LiveTVMiniPlayerProvider>
    </MiniPlayerProvider>
    </AudioPlayerProvider>
    </SpotifyProvider>
  );
}

export default App;
